var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import ErrorIcon from '@mui/icons-material/Error';
import { debounce } from '@mui/material/utils';
import { PubInputField } from 'components/Shared/PubInputField/PubInputField';
import { StyledButton } from 'components/Shared/StyledButton/StyledButton';
import { cpaService } from 'services/api/cpa-service';
import { InputFieldType } from 'models/Input/InputModels';
import { CpaSearchForm } from '../CpaSearchForm/CpaSearchForm';
import styles from '../CpaSearch.module.scss';
export function CpaSearchEditForm(_a) {
    var onSubmit = _a.onSubmit, editInProgress = _a.editInProgress;
    var _b = useState(''), customProductNbr = _b[0], setCustomProductNbr = _b[1];
    var _c = useState(false), apiCallInProgress = _c[0], setApiCallInProgress = _c[1];
    var _d = useState(false), formInError = _d[0], setFormInError = _d[1];
    var controllerRef = useRef();
    var CUSTOM_PRODUCT_NUMBER_REQUIRED = 'Custom Product Number Must Be Entered';
    var CUSTOM_PRODUCT_HELPER_DEFAULT = 'Enter an existing Custom Product Number you would like to view or edit.';
    var CUSTOM_PRODUCT_HELPER_LOCKED = "The record is currently locked because it's being edited.";
    var CUSTOM_PRODUCT_EDIT_DEFAULT = 'Edit Custom Product';
    var CUSTOM_PRODUCT_EDIT_LOCKED = 'Continue to Locked Record';
    var CUSTOM_PRODUCT_LOCKED_TEXT = "This record is locked because it's currently being edited by someone else. You can still open and view the record but won't be able to edit it.";
    var handleAbort = function () {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
        var controller = new AbortController();
        controllerRef.current = controller;
    };
    var _e = useState(CUSTOM_PRODUCT_NUMBER_REQUIRED), customError = _e[0], setCustomError = _e[1];
    var _f = useState(CUSTOM_PRODUCT_HELPER_DEFAULT), helperText = _f[0], setHelperText = _f[1];
    var _g = useState(CUSTOM_PRODUCT_EDIT_DEFAULT), buttonText = _g[0], setButtonText = _g[1];
    var _h = useState(false), isLocked = _h[0], setIsLocked = _h[1];
    var setDefaultText = function () {
        setHelperText(CUSTOM_PRODUCT_HELPER_DEFAULT);
        setButtonText(CUSTOM_PRODUCT_EDIT_DEFAULT);
        setIsLocked(false);
    };
    var setLockedText = function () {
        setHelperText(CUSTOM_PRODUCT_HELPER_LOCKED);
        setButtonText(CUSTOM_PRODUCT_EDIT_LOCKED);
        setIsLocked(true);
    };
    var handlecustomProductNbrDebounced = useMemo(function () {
        return debounce(function (text) {
            var _a;
            handleAbort();
            if (text && text.trim().length > 0) {
                setApiCallInProgress(true);
                cpaService
                    .getProductAndLockStatus(text, true, (_a = controllerRef.current) === null || _a === void 0 ? void 0 : _a.signal)
                    .then(function (rsp) {
                    setDefaultText();
                    if (rsp.productStatus !== 200) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setCustomError(rsp.productBody.message);
                    }
                    else {
                        setCustomError(undefined);
                        if (rsp.lockStatus === 200 && rsp.lockBody) {
                            if (rsp.lockBody.isLocked) {
                                setLockedText();
                            }
                        }
                    }
                    setApiCallInProgress(false);
                })
                    .catch(function () {
                    setApiCallInProgress(false);
                });
            }
            else {
                setCustomError(CUSTOM_PRODUCT_NUMBER_REQUIRED);
                setDefaultText();
            }
        }, 350);
    }, []);
    var handleEdit = function () {
        onSubmit(customProductNbr);
    };
    return (_jsxs(CpaSearchForm, { children: [_jsx(PubInputField, { fieldType: InputFieldType.STRING, validations: {
                    required: CUSTOM_PRODUCT_NUMBER_REQUIRED,
                }, customErrorMsg: customError, inProgress: apiCallInProgress, helperText: helperText, fieldValue: customProductNbr, labels: {
                    left: 'Custom Product Number',
                    right: 'Required',
                }, inError: function (val) {
                    if (formInError !== val) {
                        setFormInError(val);
                    }
                }, dirtyCheck: true, uppercaseInput: true, fieldValueUpdateHandler: function (val) {
                    var trimmedVal = val.trim();
                    setCustomProductNbr(trimmedVal);
                    handlecustomProductNbrDebounced(trimmedVal);
                } }), isLocked && (_jsxs(Box, __assign({ className: styles.lockContainer }, { children: [_jsx(ErrorIcon, { className: styles.icon }), _jsx(Box, __assign({ className: styles.lockMessage }, { children: CUSTOM_PRODUCT_LOCKED_TEXT }))] }))), _jsx("div", __assign({ className: styles.centeredButton }, { children: _jsx(StyledButton, { labelText: buttonText, disabled: formInError || editInProgress || !!customError, buttonAction: handleEdit }) }))] }));
}
